import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FlatList, RefreshControl, StyleSheet, Text, View } from 'react-native';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import timeUtils from '../../utils/timeUtils';
import { getToken } from '../../utils/token';
import ErrorBoundary from '../errorBoundaries/ErrorBoundary';
import PantryListItem from './PantryListItem';
import PantrySpinner from './PantrySpinner';
import SearchEmptyState from './SearchEmptyState';
import SearchResultItem from './SearchResultItem';

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  emptyPantryContainer: {
    marginTop: 48,
    marginLeft: 48,
    marginRight: 48,
    fontSize: 16,
    lineHeight: 22,
    color: Colors.grayInfoText,
    textAlign: 'center',
  },
  errorText: {
    marginTop: 16,
    marginHorizontal: 16,
    fontSize: 14,
    color: Colors.error || '#d32f2f',
    textAlign: 'center',
    padding: 12,
    backgroundColor: '#ffebee',
    borderRadius: 8,
  },
  errorFallbackContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorFallbackTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.error || '#d32f2f',
    marginBottom: 12,
    textAlign: 'center',
  },
  errorFallbackText: {
    fontSize: 14,
    color: Colors.grayInfoText,
    textAlign: 'center',
    lineHeight: 20,
  },
});

function PantryList(props) {
  const { dataSource, pantry, translation, locale, navigation, selectPantry, actions } = props;
  const { visits, searchQuery, loadingSearchResults, searchResultsError, pantryRegion } = pantry;
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [spinnerTimer, setSpinnerTimer] = useState(null);

  useEffect(() => {
    return function cleanup() {
      if (spinnerTimer) {
        clearTimeout(spinnerTimer);
      }
    };
  }, []);

  const choosePantry = (pantryId) => {
    if (Layout.usePantryTabs) {
      setIsLoading(true);
      navigation.navigate('Pantry', { id: pantryId });
      setSpinnerTimer(
        setTimeout(() => {
          setIsLoading(false);
        }, 1000)
      );
    } else {
      selectPantry(pantryId);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    const token = await getToken();
    const isLoggedIn = Boolean(token);

    if (isSearchMode && searchQuery) {
      // Refresh search results if in search mode with active query
      actions.searchPantries(searchQuery.trim(), {}, isLoggedIn);
      setRefreshing(false);
    } else {
      // Refresh pantries for current region in explore mode
      actions.getPantries(
        () => {
          setRefreshing(false);
        },
        isLoggedIn,
        pantryRegion
      );
    }
  };

  const today = timeUtils.getServiceDateFromDate(new Date());
  const visitsByPantryId = {};
  if (visits && visits.length) {
    visits.forEach((visit) => {
      if (visit.service_date >= today) {
        if (!visitsByPantryId[visit.pantry_id]) {
          visit.today = visit.service_date === today;
          visitsByPantryId[visit.pantry_id] = visit;
        }
      }
    });
  }

  // Determine search mode based on presence of searchQuery
  const isSearchMode = Boolean(searchQuery && searchQuery.trim());

  // Prepare data with visit information
  const enrichedData = dataSource.map((item) => {
    const enrichedItem = { ...item };
    // delete it to make sure we have no stale visit fields here
    delete enrichedItem.visit;
    if (visitsByPantryId[item.id]) {
      enrichedItem.visit = visitsByPantryId[item.id];
    }
    return enrichedItem;
  });

  // Render function for FlatList
  const renderPantryItem = ({ item }) => {
    const onSelect = () => {
      choosePantry(item.id);
    };

    // Use SearchResultItem in search mode, PantryListItem in explore mode
    if (isSearchMode) {
      return (
        <SearchResultItem
          pantry={item}
          searchQuery={searchQuery || ''}
          onPress={onSelect}
          translation={translation}
        />
      );
    } else {
      return <PantryListItem item={item} onSelect={onSelect} translation={translation} locale={locale} />;
    }
  };

  // Key extractor for FlatList
  const keyExtractor = (item) => {
    const prefix = isSearchMode ? 'SearchResultItem' : 'PantryListItem';
    return `${prefix}${item.id}`;
  };

  // Show loading spinner for search results
  if (isLoading || loadingSearchResults) {
    return <PantrySpinner translation={translation} showHeader={false} />;
  }

  // ListHeaderComponent for FlatList
  const ListHeaderComponent = () => (
    <>{searchResultsError && <Text style={styles.errorText}>{searchResultsError}</Text>}</>
  );

  // ListEmptyComponent for FlatList
  const ListEmptyComponent = () => {
    if (isSearchMode) {
      return <SearchEmptyState query={searchQuery} translation={translation} />;
    } else {
      return <Text style={styles.emptyPantryContainer}>{translation.no_pantries_available}</Text>;
    }
  };

  // Search-specific error fallback UI
  const searchErrorFallback = (
    <View style={styles.errorFallbackContainer} testID="error-fallback">
      <Text style={styles.errorFallbackTitle} testID="error-fallback-title">
        {translation.search_error_unable_to_display || 'Unable to display search results'}
      </Text>
      <Text style={styles.errorFallbackText} testID="error-fallback-text">
        {translation.search_error_occurred ||
          'An error occurred while displaying your search results. Please try searching again.'}
      </Text>
    </View>
  );

  return (
    <View style={styles.view}>
      <ErrorBoundary fallback={searchErrorFallback}>
        <FlatList
          data={enrichedData}
          renderItem={renderPantryItem}
          keyExtractor={keyExtractor}
          style={styles.scrollView}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          testID="pantries-flat-list"
          ListHeaderComponent={ListHeaderComponent}
          ListEmptyComponent={ListEmptyComponent}
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={5}
          removeClippedSubviews={true}
        />
      </ErrorBoundary>
    </View>
  );
}

PantryList.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
  navigation: PropTypes.objectOf(PropTypes.any),
  selectPantry: PropTypes.func,
  locale: PropTypes.string.isRequired,
};

export default PantryList;
